import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from 'react';
import { FiRefreshCcw } from 'react-icons/fi';

import { UseMutationResult } from '@tanstack/react-query';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';
import ICONS from '../../../constants/icons';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';

// components
import AampeTooltip from '../../shared/AampeTooltip/AampeTooltip';

const shouldForwardProps = createShouldForwardProp(['isError']);

interface StyledProps {
    width: string;
    isError: boolean;
}
interface TextProps {
    isSecondary: boolean;
}

interface TitleEditProps {
    title: React.ReactNode;
    onChange?: (value: string) => void;
    allowEmptyTitle?: boolean;
    onTitleChangeMutation?: UseMutationResult<any, any, any, any>;
    isUpdating?: boolean;
    titleToolTip?: string;
    className?: string;
    placeholder?: string;
    isSecondary?: boolean;
    isDisabled?: boolean;
    showInfoIcon?: boolean;
    onInfoIconClick?: () => void;
    showBetaModeBanner?: boolean;
}

const TitleEdit: React.FC<React.PropsWithChildren<TitleEditProps>> = ({
    title,
    onChange,
    allowEmptyTitle = true,
    onTitleChangeMutation,
    isUpdating,
    titleToolTip,
    className,
    placeholder,
    isSecondary = false,
    isDisabled,
    showInfoIcon,
    onInfoIconClick,
    showBetaModeBanner,
}) => {
    const [value, setValue] = useState<string>(title as string);

    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => setValue(title as string), [title]);

    const handleUpdate = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value);
            onTitleChangeMutation?.reset();
        },
        [onTitleChangeMutation],
    );

    const handleBlur = useCallback(() => {
        const newValue = value?.trim();
        if (newValue !== title) {
            if (newValue?.length || allowEmptyTitle) {
                if (onTitleChangeMutation) {
                    onTitleChangeMutation.mutate({ values: { name: value } });
                } else {
                    onChange?.(value);
                }
            } else {
                setValue(title as string);
            }
        }
    }, [allowEmptyTitle, onChange, onTitleChangeMutation, title, value]);

    const getWidth = useMemo(
        () => `${((value?.length ?? 0) + 1) * 16 + 32 + 2}px`,
        [value],
    );

    return (
        <TitleContainer className={className}>
            <Title>
                <TitleWithIcon>
                    {onChange || onTitleChangeMutation ? (
                        <AampeTooltip
                            arrow
                            title={
                                <StyledSpan>
                                    <strong>{value}</strong>

                                    {titleToolTip && (
                                        <span>{titleToolTip}</span>
                                    )}
                                </StyledSpan>
                            }
                            placement="bottom"
                        >
                            <Input
                                placeholder={placeholder}
                                ref={ref}
                                onBlur={handleBlur}
                                value={value}
                                disabled={isDisabled}
                                onChange={handleUpdate}
                                width={getWidth}
                                isError={!!onTitleChangeMutation?.isError}
                            />
                        </AampeTooltip>
                    ) : title && typeof title === 'string' ? (
                        <Text isSecondary={isSecondary} tabIndex={1}>
                            {title}
                        </Text>
                    ) : (
                        <> {title} </>
                    )}
                    {showBetaModeBanner && (
                        <BetaModeContainer>BETA</BetaModeContainer>
                    )}
                    {showInfoIcon && (
                        <ICONS.info
                            onClick={onInfoIconClick}
                            style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                            }}
                            width={16}
                            height={16}
                            color={aampeTheme.colors.neutral09}
                        />
                    )}
                </TitleWithIcon>

                <SpinnerContainer>
                    {isUpdating || onTitleChangeMutation?.isPending ? (
                        <FiRefreshCcw
                            color={aampeTheme.colors.neutral09}
                            size={12}
                        />
                    ) : null}
                </SpinnerContainer>
            </Title>

            {onTitleChangeMutation?.isError ? (
                <TitleError>
                    {(onTitleChangeMutation.error as any).message}
                </TitleError>
            ) : null}
        </TitleContainer>
    );
};

const TitleContainer = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 600,
}));

const Input = styled('input').withConfig({
    shouldForwardProp: shouldForwardProps,
})<StyledProps>`
    height: 100%;
    width: ${(props: StyledProps) => props.width};
    background: inherit;
    border-radius: ${aampeTheme.borderRadius.default};
    font-size: 30px;
    color: ${aampeTheme.colors.blue900};
    font-weight: 700;
    line-height: 36px;
    font-family: inherit;
    margin: 0;
    padding: ${aampeTheme.spacing(1)};
    outline: none;
    border: 1px solid transparent;

    &:hover {
        border-color: ${(props: StyledProps) =>
            props.isError
                ? aampeTheme.colors.error500
                : aampeTheme.colors.bordersWG01};
    }

    &:focus {
        border-color: ${(props: StyledProps) =>
            props.isError
                ? aampeTheme.colors.error500
                : aampeTheme.colors.solidA01};
    }
`;

const Title = styled.div(() => ({
    display: 'flex',
    alignItems: 'center',
}));

const Text = styled.h1.withConfig({
    shouldForwardProp: (prop) => prop !== 'isSecondary',
})<TextProps>(({ isSecondary }) => ({
    margin: aampeTheme.spacing(0),
    padding: aampeTheme.spacing(0),
    color: aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.title,
    fontWeight: isSecondary ? 400 : 700,
}));

const TitleError = styled.span(() => ({
    margin: aampeTheme.spacing(0.25, 0),
    padding: aampeTheme.spacing(0),
    color: aampeTheme.colors.error500,
    fontSize: 12,
}));

const SpinnerContainer = styled.div(() => ({
    width: 24,
    display: 'flex',
    alignItems: 'center',
    margin: aampeTheme.spacing(0, 1),
}));

const StyledSpan = styled.span(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
    padding: aampeTheme.spacing(1),
}));
const TitleWithIcon = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(2),
    justifyContent: 'center',
    alignItems: 'center',
});

const BetaModeContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: aampeTheme.colors.solidA02,
    fontSize: aampeTheme.size.font.textSmall,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: aampeTheme.size.lineHeight.small,
    color: aampeTheme.colors.white,
    padding: aampeTheme.spacing4(1),
    borderRadius: aampeTheme.borderRadius.default,
    height: '20px',
});
export default TitleEdit;
